import React from "react";
import { ChipProps } from "@mui/material";
import { CustomChip } from "../CustomChip";

interface IActiveInActiveStatusProps {
  status: string;
}

interface IStatusConfig {
  color: ChipProps["color"];
  label: string;
  sx?: object;
}

interface IDynamicColorItem {
  color: ChipProps["color"];
  sx: object;
}

const STATUS_MAPPINGS: Record<string, IStatusConfig> = {
  ACTIVE: { color: "success", label: "Active" },
  OPEN: { color: "warning", label: "Open" },
  CLOSED: { color: "error", label: "Closed" },
  "RE-OPENED": { color: "info", label: "Re-Opened" },
  CONNECTED: { color: "success", label: "Connected" },
  "NOT CONNECTED": { color: "error", label: "Not Connected" },
  PENDING: { color: "error", label: "Pending" },
  ACTIVATED: { color: "success", label: "Activated" },
  INACTIVE: { color: "error", label: "Inactive" },
  CANCELLED: { color: "error", label: "Cancelled" },
  "NEW SUBMISSION": { color: "success", label: "New Submission" },
  "QUOTE RECEIVED": { color: "warning", label: "Quote Received", sx: { color: "#000" } },
  APPROVED: { color: "success", label: "Approved" },
  REJECTED: { color: "error", label: "Rejected" },
  BOOKED: { color: "success", label: "Booked" },
  ONGOING: { color: "warning", label: "Ongoing", sx: { color: "#000" } },
  NEGOTIATION: { color: "warning", label: "Negotiation", sx: { color: "#000" } },
  DEAD_LEAD: { color: "error", label: "Dead Lead" },
  REACH_OUT: { color: "secondary", label: "Prospect" },
  DEAL: { color: "success", label: "Close" },
  ORDER: { color: "success", label: "Order" },
  CLOSE: { color: "success", label: "Close" },
  APPROACH: { color: "warning", label: "Approach", sx: { color: "#000" } },
  DEAL_CLOSED: { color: "secondary", label: "Deal Closed" },
  ENGAGED: { color: "warning", label: "Engaged", sx: { color: "#000" } },
  POSTPONED: { color: "primary", label: "Postponed" },
  DELETED: { color: "error", label: "Deleted" },
  WORKABLE: { color: "success", label: "Workable" },
  "NON WORKABLE": { color: "error", label: "Non Workable" },
  "NON_WORKABLE": { color: "error", label: "Non Workable" },
  "NOT WORKABLE": { color: "error", label: "Not Workable" },
  "NOT_WORKABLE": { color: "error", label: "Not Workable" },
  "CAR_SELL": { color: "success", label: "Car Sell", sx: { color: "#000" } },
  "LINK_SENT": { color: "primary", label: "Link Sent", sx: { color: "#000" } },
  MEMBERSHIP: { color: "primary", label: "Membership", sx: { color: "#000" } },
  "LINK_CLICKED": { color: "primary", label: "Link Clicked", sx: { color: "#000" } },
  "PENDING_CUSTOMER": { color: "secondary", label: "Pending Customer", sx: { color: "#000" } },
  "WORK_IN_PROGRESS": { color: "success", label: "Work In Progress", sx: { color: "#000" } },
  // Priority status/Color coding types
  CONVERTED: { color: "warning", label: "Converted", sx: { backgroundColor: "#fffde7", color: "#fbc02d", border: "1px solid #fff59d" } },
  HOT: { color: "success", label: "Hot", sx: { backgroundColor: "#e8f5e9", color: "#2e7d32", border: "1px solid #c8e6c9" } },
  REVERT_AWAITED: { color: "warning", label: "Revert Awaited", sx: { backgroundColor: "#fff3e0", color: "#b13c06ff", border: "1px solid #ffe0b2" } },
  "UNCERTAIN/FOLLOWUP": { color: "info", label: "Uncertain/Followup", sx: { backgroundColor: "#e0f7fa", color: "#00838f", border: "1px solid #b2ebf2" } },
  COLD: { color: "error", label: "Cold", sx: { backgroundColor: "#ffebee", color: "#c62828", border: "1px solid #ffcdd2" } },
  REFERRED: { color: "primary", label: "Referred", sx: { backgroundColor: "#f3e5f5", color: "#8e24aa", border: "1px solid #e1bee7" } },
  SUSPECT: { color: "info", label: "Suspect" },
  PROSPECT: { color: "info", label: "Prospect", sx: { backgroundColor: "#e3f2fd", color: "#1565c0", border: "1px solid #bbdefb" } },
  // Specific lead types mapping
  "INSTANT CAR REPAIR": { color: "warning", label: "Instant Car Repair", sx: { backgroundColor: "#fff3e0", color: "#e65100", border: "1px solid #ffe0b2" } },
  INSTANT_CAR_REPAIR: { color: "warning", label: "Instant Car Repair", sx: { backgroundColor: "#fff3e0", color: "#e65100", border: "1px solid #ffe0b2" } },
  "FUEL DELIVERY": { color: "success", label: "Fuel Delivery", sx: { backgroundColor: "#e8f5e9", color: "#2e7d32", border: "1px solid #c8e6c9" } },
  FUEL_DELIVERY: { color: "success", label: "Fuel Delivery", sx: { backgroundColor: "#e8f5e9", color: "#2e7d32", border: "1px solid #c8e6c9" } },
  TOWING: { color: "primary", label: "Towing", sx: { backgroundColor: "#f3e5f5", color: "#7b1fa2", border: "1px solid #e1bee7" } },
  "TOWING SERVICE": { color: "primary", label: "Towing Service", sx: { backgroundColor: "#f3e5f5", color: "#7b1fa2", border: "1px solid #e1bee7" } },
  "BATTERY JUMPSTART": { color: "secondary", label: "Battery Jumpstart", sx: { backgroundColor: "#e0f2f1", color: "#00695c", border: "1px solid #b2dfdb" } },
  BATTERY_JUMPSTART: { color: "secondary", label: "Battery Jumpstart", sx: { backgroundColor: "#e0f2f1", color: "#00695c", border: "1px solid #b2dfdb" } },
  "BATTERY JUMP START": { color: "secondary", label: "Battery Jump Start", sx: { backgroundColor: "#e0f2f1", color: "#00695c", border: "1px solid #b2dfdb" } },
  BATTERY_JUMP_START: { color: "secondary", label: "Battery Jump Start", sx: { backgroundColor: "#e0f2f1", color: "#00695c", border: "1px solid #b2dfdb" } },
  "TYRE CHANGE": { color: "error", label: "Tyre Change", sx: { backgroundColor: "#fbe9e7", color: "#d84315", border: "1px solid #ffccbc" } },
  "FLAT TYRE": { color: "error", label: "Flat Tyre", sx: { backgroundColor: "#fbe9e7", color: "#d84315", border: "1px solid #ffccbc" } },
  "KEY LOCKOUT": { color: "primary", label: "Key Lockout", sx: { backgroundColor: "#ede7f6", color: "#4527a0", border: "1px solid #d1c4e9" } },
  INSPECTION: { color: "info", label: "Inspection", sx: { backgroundColor: "#e0f7fa", color: "#00838f", border: "1px solid #b2ebf2" } },
  "QUICK RSA": { color: "secondary", label: "Quick RSA", sx: { backgroundColor: "#e0f2f1", color: "#00695c", border: "1px solid #b2dfdb" } },
  QUICK_RSA: { color: "secondary", label: "Quick RSA", sx: { backgroundColor: "#e0f2f1", color: "#00695c", border: "1px solid #b2dfdb" } },
  "PANEL LEAD": { color: "primary", label: "Panel Lead", sx: { backgroundColor: "#ede7f6", color: "#4527a0", border: "1px solid #d1c4e9" } },
  PANEL_LEAD: { color: "primary", label: "Panel Lead", sx: { backgroundColor: "#ede7f6", color: "#4527a0", border: "1px solid #d1c4e9" } },
  // Lead sources mapping
  GOOGLE: { color: "info", label: "Google", sx: { backgroundColor: "#e8f0fe", color: "#1a73e8", border: "1px solid #aecbfa" } },
  WEBSITE: { color: "primary", label: "Website", sx: { backgroundColor: "#e8eaf6", color: "#283593", border: "1px solid #c5cae9" } },
  WEB: { color: "primary", label: "Web", sx: { backgroundColor: "#e8eaf6", color: "#283593", border: "1px solid #c5cae9" } },
  APP: { color: "success", label: "App", sx: { backgroundColor: "#e8f5e9", color: "#2e7d32", border: "1px solid #c8e6c9" } },
  MOBILE_APP: { color: "success", label: "Mobile App", sx: { backgroundColor: "#e8f5e9", color: "#2e7d32", border: "1px solid #c8e6c9" } },
  "MOBILE APP": { color: "success", label: "Mobile App", sx: { backgroundColor: "#e8f5e9", color: "#2e7d32", border: "1px solid #c8e6c9" } },
  WHATSAPP: { color: "secondary", label: "WhatsApp", sx: { backgroundColor: "#e0f2f1", color: "#00695c", border: "1px solid #b2dfdb" } },
  CALL: { color: "warning", label: "Call", sx: { backgroundColor: "#fff3e0", color: "#e65100", border: "1px solid #ffe0b2" } },
  CROSSROADS_DIALER: { color: "warning", label: "Crossroads Dialer", sx: { backgroundColor: "#fff3e0", color: "#e65100", border: "1px solid #ffe0b2" } },
  MANUAL: { color: "secondary", label: "Manual", sx: { backgroundColor: "#f3e5f5", color: "#7b1fa2", border: "1px solid #e1bee7" } },
  CRM: { color: "info", label: "CRM", sx: { backgroundColor: "#e0f7fa", color: "#00838f", border: "1px solid #b2ebf2" } },
  FACEBOOK: { color: "primary", label: "Facebook", sx: { backgroundColor: "#e7f3ff", color: "#1877f2", border: "1px solid #b8d6fd" } },
  META: { color: "primary", label: "Meta", sx: { backgroundColor: "#e7f3ff", color: "#1877f2", border: "1px solid #b8d6fd" } },
  INSTAGRAM: { color: "secondary", label: "Instagram", sx: { backgroundColor: "#fce4ec", color: "#d81b60", border: "1px solid #f8bbd0" } },
  LINKEDIN: { color: "info", label: "LinkedIn", sx: { backgroundColor: "#e1f5fe", color: "#0277bd", border: "1px solid #b3e5fc" } },
};

const DYNAMIC_COLOR_PALETTE: IDynamicColorItem[] = [
  { color: "info", sx: { backgroundColor: "#e3f2fd", color: "#1565c0", border: "1px solid #bbdefb" } },
  { color: "warning", sx: { backgroundColor: "#fff3e0", color: "#e65100", border: "1px solid #ffe0b2" } },
  { color: "success", sx: { backgroundColor: "#e8f5e9", color: "#2e7d32", border: "1px solid #c8e6c9" } },
  { color: "primary", sx: { backgroundColor: "#f3e5f5", color: "#7b1fa2", border: "1px solid #e1bee7" } },
  { color: "secondary", sx: { backgroundColor: "#e0f2f1", color: "#00695c", border: "1px solid #b2dfdb" } },
  { color: "error", sx: { backgroundColor: "#fbe9e7", color: "#d84315", border: "1px solid #ffccbc" } },
  { color: "primary", sx: { backgroundColor: "#ede7f6", color: "#4527a0", border: "1px solid #d1c4e9" } },
  { color: "info", sx: { backgroundColor: "#e0f7fa", color: "#00838f", border: "1px solid #b2ebf2" } },
  { color: "warning", sx: { backgroundColor: "#fffde7", color: "#fbc02d", border: "1px solid #fff59d" } },
  { color: "error", sx: { backgroundColor: "#fce4ec", color: "#c2185b", border: "1px solid #f8bbd0" } },
  { color: "secondary", sx: { backgroundColor: "#eceff1", color: "#37474f", border: "1px solid #cfd8dc" } },
  { color: "info", sx: { backgroundColor: "#e8eaf6", color: "#3f51b5", border: "1px solid #c5cae9" } },
];

const getDynamicStyle = (str: string): IDynamicColorItem => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return DYNAMIC_COLOR_PALETTE[Math.abs(hash) % DYNAMIC_COLOR_PALETTE.length];
};

export const StatusRenderer: React.FC<IActiveInActiveStatusProps> = ({ status }) => {
  if (!status || !status.trim() || status.toUpperCase() === "N/A") {
    return <>--</>;
  }

  const rawUpper = status.trim().toUpperCase();
  const spaceUpper = rawUpper.replace(/_/g, " ");
  const underscoreUpper = rawUpper.replace(/\s+/g, "_");

  const mapping =
    STATUS_MAPPINGS[rawUpper] ||
    STATUS_MAPPINGS[spaceUpper] ||
    STATUS_MAPPINGS[underscoreUpper];

  if (mapping) {
    return (
      <CustomChip
        color={mapping.color}
        content={mapping.label}
        size="small"
        {...(mapping.sx ? { sx: mapping.sx } : {})}
      />
    );
  }

  const dynamic = getDynamicStyle(rawUpper);
  return (
    <CustomChip
      color={dynamic.color}
      content={status}
      size="small"
      sx={dynamic.sx}
    />
  );
};
